
import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { Loader2, Clock, User, AlertTriangle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

function AppVersionHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [limit, setLimit] = useState(20);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, [limit]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        API_ENDPOINTS.appConfigHistory(`limit=${limit}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data?.data?.history ?? [];
      setHistory(data);
      setHasMore(data.length >= limit);
    } catch (err) {
      setError("Failed to load configuration history");
      console.error("Error fetching history:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    // Relative time
    let relative = "";
    if (diffSecs < 60) {
      relative = "just now";
    } else if (diffMins < 60) {
      relative = `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
    } else if (diffHours < 24) {
      relative = `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    } else if (diffDays < 30) {
      relative = `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    } else {
      relative = date.toLocaleDateString();
    }

    // Full datetime
    const fullDate = date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    return { relative, fullDate };
  };

  const getChangedFields = (config, previousConfig) => {
    if (!previousConfig) return null;

    const changes = [];
    const fields = [
      { key: "minimumRequiredVersion", label: "Min Version" },
      { key: "latestVersion", label: "Latest Version" },
      { key: "forceUpdateMessage", label: "Update Message" },
      { key: "showUpdatePrompt", label: "Show Prompt" },
      { key: "isForced", label: "Force Update" },
    ];

    fields.forEach(({ key, label }) => {
      const oldVal = previousConfig[key];
      const newVal = config[key];
      if (oldVal !== newVal) {
        changes.push({
          field: label,
          from: oldVal === undefined || oldVal === null ? "—" : String(oldVal),
          to: newVal === undefined || newVal === null ? "—" : String(newVal),
        });
      }
    });

    return changes;
  };

  if (loading && history.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="bg-white p-12 rounded-lg shadow-md text-center">
        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No History Yet</h3>
        <p className="text-gray-600">
          Configuration changes will appear here once you save updates.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Configuration Change History</h3>
        <span className="text-sm text-gray-600">{history.length} record{history.length !== 1 ? "s" : ""}</span>
      </div>

      <div className="space-y-4">
        {history.map((entry, index) => {
          const { relative, fullDate } = formatTimestamp(entry.timestamp);
          const changes = getChangedFields(entry.config, entry.previousConfig);
          const isForced = entry.config?.isForced === true;

          return (
            <div
              key={entry._id || index}
              className={`bg-white border-2 rounded-lg shadow-sm hover:shadow-md transition-shadow ${
                isForced ? "border-amber-300" : "border-gray-200"
              }`}
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${isForced ? "bg-amber-100" : "bg-blue-100"}`}>
                      <Clock className={`w-5 h-5 ${isForced ? "text-amber-600" : "text-blue-600"}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">{relative}</span>
                        {isForced && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-200 text-amber-800">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            FORCE UPDATE
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{fullDate}</p>
                    </div>
                  </div>
                </div>

                {/* Changed By */}
                {entry.changedBy && (
                  <div className="flex items-center gap-2 mb-3 text-sm">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700">
                      Changed by: <span className="font-medium">{entry.changedBy.email || entry.changedBy.username || "Unknown"}</span>
                    </span>
                  </div>
                )}

                {/* Change Reason */}
                {entry.changeReason && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-900 mb-1">Reason for Change:</p>
                    <p className="text-sm text-blue-800">{entry.changeReason}</p>
                  </div>
                )}

                {/* Changes Summary */}
                {changes && changes.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Changes:</p>
                    <div className="space-y-1.5">
                      {changes.map((change, idx) => (
                        <div key={idx} className="text-sm flex items-start gap-2 pl-4">
                          <span className="text-gray-500">•</span>
                          <span className="flex-1">
                            <span className="font-medium text-gray-800">{change.field}:</span>{" "}
                            <span className="text-red-600 line-through">{change.from}</span>{" "}
                            <span className="text-gray-500">→</span>{" "}
                            <span className="text-green-600 font-medium">{change.to}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Config Snapshot (collapsible) */}
                <details className="mt-3 group">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-1">
                    <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                    View Full Configuration
                  </summary>
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <pre className="text-xs text-gray-700 overflow-x-auto">
                      {JSON.stringify(entry.config, null, 2)}
                    </pre>
                  </div>
                </details>
              </div>
            </div>
          );
        })}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <Button
            onClick={() => setLimit((prev) => prev + 20)}
            disabled={loading}
            variant="outline"
            className="flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                Load More
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default AppVersionHistory;
